import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Button from '../../components/button';
import Warning from '../../components/warning';
import Translator from '../../services/translate-factory';
import styles from './TwoFactorModal.module.css';

const T = Translator.create();
type TwoFactorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void>;
  onResendCode?: () => Promise<void>;
  method?: 'email' | 'authenticator' | 'sms';
  email?: string;
  phoneNumber?: string;
};

const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onResendCode,
  method = 'email',
  email,
  phoneNumber,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const [isExpired, setIsExpired] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isOpen) {
      startTimer();

      interval = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) {
            setIsExpired(true);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isOpen]);

  const startTimer = () => {
    setCode('');
    setError('');
    setTimeLeft(180);
    setIsLoading(false);
    setIsExpired(false);
    setIsResending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Trigger submit when Enter is pressed
      handleSubmit();
    } else if (e.key === 'Backspace') {
      e.preventDefault();

      // Clear error when user starts modifying the code
      if (error) {
        setError('');
      }

      const arr = code.split('');

      // If current input is empty and we're not at the first input, move to previous
      if (!code[idx] && idx > 0) {
        arr[idx - 1] = '';
        const prev = document.getElementById(`twofa-code-${idx - 1}`);
        if (prev) (prev as HTMLInputElement).focus();
      } else {
        // Clear current input
        arr[idx] = '';
      }

      setCode(arr.join(''));
    } else if (e.key === 'Delete') {
      e.preventDefault();

      // Clear error when user starts modifying the code
      if (error) {
        setError('');
      }

      const arr = code.split('');
      arr[idx] = '';
      setCode(arr.join(''));
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      const prev = document.getElementById(`twofa-code-${idx - 1}`);
      if (prev) (prev as HTMLInputElement).focus();
    } else if (e.key === 'ArrowRight' && idx < 7) {
      e.preventDefault();
      const next = document.getElementById(`twofa-code-${idx + 1}`);
      if (next) (next as HTMLInputElement).focus();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    // Clear error when user starts typing
    if (error) {
      setError('');
    }

    // Allow alphanumeric only and convert to uppercase
    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const arr = code.split('');

    // If backspace was pressed, val will be empty
    if (!val) {
      arr[idx] = '';
    } else {
      // Take the last character if multiple characters were pasted
      arr[idx] = val.slice(-1);

      // Move to next input if we have a value and there's a next input
      if (idx < 7) {
        const next = document.getElementById(`twofa-code-${idx + 1}`);
        if (next) (next as HTMLInputElement).focus();
      }
    }

    setCode(arr.join('').slice(0, 8));
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, 8);
    if (pastedData) {
      setCode(pastedData);
      const lastInput = document.getElementById(`twofa-code-${Math.min(pastedData.length, 7)}`);
      if (lastInput) (lastInput as HTMLInputElement).focus();
    }
  };

  const handleResendCode = async () => {
    if (!onResendCode) return;

    setIsResending(true);
    setError('');
    try {
      await onResendCode();
      // Reset timer state - the useEffect will handle restarting the interval
      setCode('');
      setError('');
      setTimeLeft(180);
      setIsExpired(false);
    } catch (err) {
      setError(T.t('2fa_error_resend_failed'));
    }
    setIsResending(false);
  };

  const handleSubmit = async () => {
    if (code.length !== 8) {
      setError(T.t('2fa_error_empty_code'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await onSubmit(code);
    } catch (err: any) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose} className="text-center">
        {T.t('2fa_title')}
      </ModalHeader>
      <ModalBody>
        <div
          className="text-center mb-3"
          dangerouslySetInnerHTML={{ __html: T.t('2fa_sent_to_email', { email: email || '' }) }}
        />
        {!isExpired ? (
          <Warning show icon="timer" classNames="text-center mb-3">
            {T.t('2fa_code_valid_for', {
              minutes: Math.floor(timeLeft / 60),
              seconds: (timeLeft % 60).toString().padStart(2, '0'),
            })}
          </Warning>
        ) : (
          <Warning show type="danger" icon="error_outline" classNames="text-center mb-3">
            {T.t('2fa_code_expired')}
          </Warning>
        )}
        {error && (
          <Warning show type="danger" icon="error_outline" classNames="text-center mb-3">
            {error}
          </Warning>
        )}
        <div className={styles.container} onPaste={handlePaste}>
          <div className={styles.inputGroup}>
            {[...Array(8)].map((_, i) => (
              <input
                key={i}
                id={`twofa-code-${i}`}
                type="text"
                inputMode="text"
                pattern="[0-9]*"
                maxLength={1}
                value={code[i] || ''}
                onChange={(e) => handleInput(e, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                className={`${styles.input} ${code[i] ? styles.inputFilled : ''} ${isExpired || error ? styles.inputError : ''} ${
                  isExpired ? styles.inputExpired : ''
                }`.trim()}
                autoFocus={i === 0}
                disabled={isLoading || isExpired}
                aria-label={`Verification code digit ${i + 1}`}
              />
            ))}
          </div>
        </div>
        <div className={`text-center mt-3 ${styles.helpText}`}>{T.t('2fa_footer_help')}</div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isLoading}>
          {T.t('gen_cancel')}
        </Button>
        {onResendCode && (
          <Button
            color="primary"
            onClick={handleResendCode}
            disabled={!isExpired || isResending}
            icon={isResending ? 'hourglass_empty' : undefined}
          >
            {isResending ? T.t('gen_resending') : T.t('2fa_resend_code')}
          </Button>
        )}
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isLoading || code.length !== 8 || isExpired}
          icon={isLoading ? 'hourglass_empty' : undefined}
        >
          {isLoading ? T.t('gen_verifying') : T.t('gen_verify')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TwoFactorModal;
